/**
 * Date-period boundaries for the dashboard toolbar. Returns a local [from, to)
 * window (to is exclusive) converted to UTC ISO strings for the API.
 */
export type DashboardPeriod = 'week' | 'month' | 'year' | 'custom';

export interface DateRange {
  /** UTC ISO string, inclusive */
  from: string;
  /** UTC ISO string, exclusive */
  to: string;
}

/** Local Date -> UTC ISO string (the API compares naive UTC). */
export function localToUtcIso(date: Date): string {
  return date.toISOString();
}

/** Start of the ISO week (Monday 00:00) containing `ref`, in local time. */
function startOfIsoWeek(ref: Date): Date {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const day = d.getDay(); // 0=Sun..6=Sat
  const diffToMonday = (day + 6) % 7; // Mon=0, Sun=6
  d.setDate(d.getDate() - diffToMonday);
  return d;
}

/**
 * Compute the [from, to) window for a preset period, relative to `now` (local).
 * `now` is injectable so the logic is deterministic under test.
 */
export function getPresetRange(period: Exclude<DashboardPeriod, 'custom'>, now: Date): DateRange {
  let from: Date;
  let to: Date;

  switch (period) {
    case 'week': {
      from = startOfIsoWeek(now);
      to = new Date(from);
      to.setDate(to.getDate() + 7);
      break;
    }
    case 'month': {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      break;
    }
    case 'year': {
      from = new Date(now.getFullYear(), 0, 1);
      to = new Date(now.getFullYear() + 1, 0, 1);
      break;
    }
  }

  return { from: localToUtcIso(from), to: localToUtcIso(to) };
}

/** Build a range from an explicit local custom selection (to is made exclusive: end-of-day + 1). */
export function getCustomRange(fromLocal: Date, toLocal: Date): DateRange {
  const from = new Date(fromLocal.getFullYear(), fromLocal.getMonth(), fromLocal.getDate());
  const toExclusive = new Date(toLocal.getFullYear(), toLocal.getMonth(), toLocal.getDate() + 1);
  return { from: localToUtcIso(from), to: localToUtcIso(toExclusive) };
}
